import {
  Box,
  Container,
  Grid,
  Typography,
  Stack,
  useMediaQuery,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { useTheme } from "@mui/material/styles";
import { useRef } from "react";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";
import { SubscriptionForm } from "./subscription";
import { useTranslation } from "react-i18next";
import EventbriteWidget from "./eventbrightwidget";

const Page = () => {
  usePageView();
  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { t } = useTranslation();

  const subscibre = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (subscibre.current) {
      subscibre.current.scrollIntoView({ behavior: "smooth" });
      return;
    }
  };

  return (
    <>
      <Seo title={t("page_title")} />
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
          backgroundColor: (theme) =>
            theme.palette.mode === "dark" ? "neutral.900" : "neutral.100",
        }}
      >
        {/* Header Section with Background Image */}
        <Box
          sx={{
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundImage: "url(assets/gallery/workshopclass.jpg)",
            color: "common.black",
            textAlign: "center",
            position: "relative",
            py: mdUp ? 20 : 10,
          }}
        >
          <Box
            sx={{
              background:
                "linear-gradient(to bottom, white, rgba(0, 0, 0, 0.1))",
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1,
            }}
          />
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
            <Box
              sx={{
                backgroundColor: "common.white",
                borderRadius: 4,
                height: mdUp ? "400px" : "",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0.8,
                border: 3,
                m: 3,
                p: 3,
                borderColor: "primary.main",
              }}
            >
              <Typography
                variant="h3"
                sx={{ fontWeight: "bold", mb: 2, color: "primary.main" }}
              >
                {t("header_title")}
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 4 }}>
                {t("header_subtitle")}
              </Typography>
              <Grid container spacing={3} sx={{ color: "black" }}>
                <Grid item xs={12} md={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      mx: 2,
                      p: 2,
                      borderRadius: 2,
                      color: "common.black",
                      ":hover": {
                        backgroundColor: "primary.light",
                        color: "common.black",
                      },
                    }}
                  >
                    <a
                      href="https://maps.app.goo.gl/DZffmZkAwvmUiUUFA"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: "none", color: "black" }}
                    >
                      <LocationCityIcon fontSize="large" sx={{ mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("header_location_title")}
                      </Typography>
                      <Typography variant="body1">
                        {t("header_location_place")}
                      </Typography>
                    </a>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      mx: 2,
                      p: 2,
                      borderRadius: 2,
                      color: "common.black",
                      ":hover": {
                        backgroundColor: "primary.light",
                        color: "common.black",
                      },
                    }}
                  >
                    <a
                      href="https://calendar.app.google/vb1uqfk5ryLWxTQw9"
                      target="_blank"
                      rel="noreferrer"
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <CalendarMonthIcon fontSize="large" sx={{ mb: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("header_date_title")}
                      </Typography>
                      <Typography variant="body1">
                        {t("header_date_detail")}
                      </Typography>
                    </a>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    sx={{
                      mx: 2,
                      p: 2,
                      borderRadius: 2,
                      color: "common.black",
                      ":hover": {
                        backgroundColor: "primary.light",
                        color: "common.black",
                        cursor: "pointer",
                      },
                    }}
                    onClick={() => handleScroll()}
                  >
                    <HowToRegIcon fontSize="large" sx={{ mb: 1 }} />
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      {t("header_registration_title")}
                    </Typography>
                    <Typography variant="body1">
                      {t("header_registration_detail")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* Content Section */}
        <Box
          sx={{
            pt: 8,
            pb: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "neutral.800" : "white",
          }}
          ref={subscibre}
        >
          <Container maxWidth="md">
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t("content_intro")}
            </Typography>
            <Stack
              direction={mdUp ? "row" : "column"}
              sx={{
                my: 4,
                display: "flex",
                justifyContent: "center",
              }}
              spacing={5}
            >
              <img
                height={mdUp ? "300px" : ""}
                width={mdUp ? "" : "50%"}
                src="assets/gallery/androidapsscreenshot.png"
                alt="workshop"
              />
              <img
                height={mdUp ? "300px" : ""}
                width={mdUp ? "" : "50%"}
                src="assets/gallery/smartwatch.jpg"
                alt="workshop"
              />
              <img
                height={mdUp ? "300px" : ""}
                width={mdUp ? "" : "50%"}
                src="assets/gallery/heatmap.png"
                alt="heatmap"
              />
            </Stack>
            {/* <Typography variant="h5" sx={{ mb: 1 }}>
              {t("content_empowerment_title")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t("content_empowerment_detail")}
            </Typography>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {t("content_inclusivity_title")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t("content_inclusivity_detail")}
            </Typography>
            <Typography variant="h5" sx={{ mb: 1 }}>
              {t("content_collaboration_title")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 4 }}>
              {t("content_collaboration_detail")}
            </Typography> */}
          </Container>
        </Box>

        {/* Subscription Section */}
        <Box
          sx={{
            pb: 4,
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "neutral.800" : "white",
          }}
          ref={subscibre}
        >
          <Container maxWidth="md">
            <EventbriteWidget />
            {/* <SubscriptionForm /> */}
          </Container>
        </Box>

        {/* Footer Section */}
        <Box
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "neutral.800" : "neutral.50",
            py: 4,
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} {t("page_title")}. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </>
  );
};

export default Page;
